/**
 * Nav-section registry derived entirely from workspace manifests.
 * No hardcoded per-app switch needed — add navSections / resources to
 * <Name>Workspace.manifest.ts instead.
 */
import { APP_MANIFEST as FBR_SMART }     from './FbrSmartWorkspace.manifest';
import { APP_MANIFEST as PRA_WORKSPACE } from './PRAWorkSpaceApp.manifest';
import { APP_MANIFEST as ACCOUNTING }    from './AccountingWorkspace.manifest';
import { APP_MANIFEST as FUELING }       from './FuelingStationWorkspace.manifest';
import { APP_MANIFEST as SETTINGS }      from './SettingsWorkspace.manifest';
import { APP_MANIFEST as AUTO_DEALERS }  from './AutoDealersWorkspace.manifest';
import { APP_MANIFEST as PURCHASES }     from './PurchasesWorkspace.manifest';
import { APP_MANIFEST as AUTO_WORKSHOP } from './AutoWorkshopWorkspace.manifest';
import { APP_MANIFEST as INVENTORY }     from './InventoryWorkspace.manifest';
import { APP_MANIFEST as SALES }         from './SalesWorkspace.manifest';
import { APP_MANIFEST as PROJECT_MANAGEMENT } from './ProjectManagementWorkspace.manifest';
import { APP_MANIFEST as DOCUMENT_MANAGEMENT } from './DocumentManagementWorkspace.manifest';
import { APP_MANIFEST as PAYROLL } from './PayrollWorkspace.manifest';

import type { WorkspaceAppManifest } from './workspaceManifest';

/** All workspace manifests in launcher order. */
const ALL_MANIFESTS: WorkspaceAppManifest[] = [
    FBR_SMART,
    PRA_WORKSPACE,
    ACCOUNTING,
    FUELING,
    AUTO_DEALERS,
    PURCHASES,
    AUTO_WORKSHOP,
    INVENTORY,
    SALES,
    PROJECT_MANAGEMENT,
    PAYROLL,
    DOCUMENT_MANAGEMENT,
    SETTINGS,
];

// ── Lookup tables built once at module load ──────────────────────────────────

/**
 * Map: navSection route → { appId, navSectionKey, label, permissionsPrefix }
 * Used for label lookups and membership checks.
 */
const _routeToNavSection = new Map<
    string,
    { appId: string; navSectionKey: string; label: string; permissionsPrefix: string; appDisplayName: string }
>();

/**
 * Map: `${permissionsPrefix}::${navSectionKey}` → resource keys in that section.
 * Used by useCanAccessAnyInNavSection and injectImplicitReadRights.
 */
const _navSectionResources = new Map<string, string[]>();

/**
 * Map: `${permissionsPrefix}::${resourceKey}` → navSection key (or undefined).
 * Used for grouping in the Access Rights panel.
 */
const _resourceNavSection = new Map<string, string>();

for (const manifest of ALL_MANIFESTS) {
    for (const ns of manifest.navSections) {
        _routeToNavSection.set(ns.route, {
            appId: manifest.appId,
            navSectionKey: ns.key,
            label: ns.label,
            permissionsPrefix: manifest.permissionsPrefix,
            appDisplayName: manifest.displayName,
        });
    }

    for (const r of manifest.resources) {
        if (r.navSection) {
            const sectionMapKey = `${manifest.permissionsPrefix}::${r.navSection}`;
            if (!_navSectionResources.has(sectionMapKey)) {
                _navSectionResources.set(sectionMapKey, []);
            }
            _navSectionResources.get(sectionMapKey)!.push(r.key);

            _resourceNavSection.set(`${manifest.permissionsPrefix}::${r.key}`, r.navSection);
        }
    }
}

// ── Public types ─────────────────────────────────────────────────────────────

export type RegisteredNavSectionEntry = {
    /**
     * Route stored in GroupMenuGrant.MenuKey when granted to a group
     * (e.g. '/accounting/transaction').
     */
    route: string;
    /** Stable navSection key within the app (e.g. 'transaction'). */
    navSectionKey: string;
    /** Human-readable display label (e.g. 'Transaction'). */
    label: string;
    appId: string;
    appDisplayName: string;
    permissionsPrefix: string;
};

// ── Public functions ─────────────────────────────────────────────────────────

/**
 * All registered nav sections across all apps (or a single app if appId provided).
 * Returns entries in manifest order (launcher order, sections order within each app).
 */
export function getAllNavSections(appId?: string): RegisteredNavSectionEntry[] {
    const out: RegisteredNavSectionEntry[] = [];
    for (const manifest of ALL_MANIFESTS) {
        if (appId && manifest.appId !== appId) continue;
        for (const ns of manifest.navSections) {
            out.push({
                route: ns.route,
                navSectionKey: ns.key,
                label: ns.label,
                appId: manifest.appId,
                appDisplayName: manifest.displayName,
                permissionsPrefix: manifest.permissionsPrefix,
            });
        }
    }
    return out;
}

/**
 * Display label for a nav section route (stored in GroupMenuGrant.MenuKey).
 * Returns the route itself if not found (forward-compat with unknown future routes).
 */
export function navSectionLabelForRoute(route: string): string {
    const entry = _routeToNavSection.get(route);
    if (!entry) return route;
    return `${entry.appDisplayName} / ${entry.label}`;
}

/**
 * All model resource keys in a given nav section.
 * Used by useCanAccessAnyInNavSection and injectImplicitReadRights.
 */
export function getResourceKeysForNavSection(
    permissionsPrefix: string,
    navSectionKey: string
): string[] {
    return _navSectionResources.get(`${permissionsPrefix}::${navSectionKey}`) ?? [];
}

/**
 * Which navSection key a resource belongs to, or null if not declared.
 * Used by AppPermissionPanel to insert section dividers.
 */
export function getNavSectionForResource(
    permissionsPrefix: string,
    resourceKey: string
): string | null {
    return _resourceNavSection.get(`${permissionsPrefix}::${resourceKey}`) ?? null;
}

/**
 * Resolve a navSection route back to its navSectionKey and permissionsPrefix,
 * or null if the route is not a registered nav section.
 */
export function resolveNavSectionRoute(
    route: string
): { permissionsPrefix: string; navSectionKey: string } | null {
    const entry = _routeToNavSection.get(route);
    if (!entry) return null;
    return { permissionsPrefix: entry.permissionsPrefix, navSectionKey: entry.navSectionKey };
}

/**
 * Map: permissionsPrefix → navSection key for each registered manifest.
 * Used internally by nav components to look up section keys from their prefix.
 */
const _prefixToManifest = new Map<string, WorkspaceAppManifest>();
for (const manifest of ALL_MANIFESTS) {
    _prefixToManifest.set(manifest.permissionsPrefix, manifest);
}

/** Nav sections for a given permissionsPrefix (for legacy callers that have a prefix). */
export function getNavSectionsByPrefix(permissionsPrefix: string): RegisteredNavSectionEntry[] {
    const manifest = _prefixToManifest.get(permissionsPrefix);
    if (!manifest) return [];
    return manifest.navSections.map(ns => ({
        route: ns.route,
        navSectionKey: ns.key,
        label: ns.label,
        appId: manifest.appId,
        appDisplayName: manifest.displayName,
        permissionsPrefix: manifest.permissionsPrefix,
    }));
}
